import type { Card } from './types';
import type { DeckManager } from './DeckManager';
import { getSessionUser, signInOrCreateWithPassword, logout, saveDeck, listDecks, loadDeck, deleteDeck } from './supabaseDecklists';
import { supabase } from './supabaseClient';

export class UIManager {
  private deckManager: DeckManager;
  private cards: Map<number, Card>;
  // For double-faced cards, the deck identity (base) can differ from the face currently displayed.
  private currentCardBase: Card | null = null;
  private currentCardShown: Card | null = null;
  private showBackFace: boolean = false; // FIX #3: État flip

  constructor(deckManager: DeckManager, cards: Card[]) {
    this.deckManager = deckManager;
    this.cards = new Map(cards.map(c => [c.number, c]));
    this.init();
  }

  private init(): void {
    
    this.initDeckPanelToggle();
// Clear button
    const btnClear = document.getElementById('btnClear');
    btnClear?.addEventListener('click', () => {
      if (confirm('Vider le deck ?')) {
        this.deckManager.clear();
      }
    });

    // Export button
    const btnExport = document.getElementById('btnExport');
    btnExport?.addEventListener('click', () => {
      const text = this.deckManager.exportText();
      this.copyToClipboard(text);
      this.showToast('Deck copié dans le presse-papier !');
    });

    // Import button
    const btnImport = document.getElementById('btnImport');
    btnImport?.addEventListener('click', () => {
      this.openImportModal();
    });

    // Supabase (cloud save/load)
    this.initSupabaseButtons();

    // PWA refresh (force SW update + reload)
    const btnPwaRefresh = document.getElementById('btnPwaRefresh');
    btnPwaRefresh?.addEventListener('click', async () => {
      await this.hardRefreshPWA();
    });

    // Deck name input
    const deckNameInput = document.getElementById('deckNameInput') as HTMLInputElement;
    if (deckNameInput) {
      // Charger le nom initial
      deckNameInput.value = this.deckManager.getDeckName();
      
      // Sauvegarder quand on modifie le nom
      deckNameInput.addEventListener('input', () => {
        this.deckManager.setDeckName(deckNameInput.value);
      });
      
      deckNameInput.addEventListener('blur', () => {
        this.deckManager.setDeckName(deckNameInput.value);
      });
    }

    // Import modal controls
    const importModalClose = document.getElementById('importModalClose');
    const importModalBackdrop = document.getElementById('importModalBackdrop');
    const importCancelBtn = document.getElementById('importCancelBtn');
    const importConfirmBtn = document.getElementById('importConfirmBtn');

    importModalClose?.addEventListener('click', () => this.closeImportModal());
    importModalBackdrop?.addEventListener('click', () => this.closeImportModal());
    importCancelBtn?.addEventListener('click', () => this.closeImportModal());
    importConfirmBtn?.addEventListener('click', () => this.confirmImport());


    // Modal close
    const modalClose = document.getElementById('modalClose');
    const modalBackdrop = document.getElementById('modalBackdrop');
    
    modalClose?.addEventListener('click', () => this.closeModal());
    modalBackdrop?.addEventListener('click', () => this.closeModal());

    // Modal actions
    const modalAddBtn = document.getElementById('modalAddBtn');
    const modalRemoveBtn = document.getElementById('modalRemoveBtn');

    modalAddBtn?.addEventListener('click', () => {
      if (this.currentCardBase) {
        const t = String(this.currentCardBase.type || '').toLowerCase();
        if (t === 'token') {
          this.showToast("Un token ne peut pas être ajouté au deck.");
          return;
        }
        if (this.deckManager.addCard(this.currentCardBase.number)) {
          this.showToast('Carte ajoutée !');
          this.updateModalDeckBadge(); // Mettre à jour l'indicateur du modal
        } else {
          const rarity = this.currentCardBase.rarity?.toUpperCase() || 'C';
          const max = rarity === 'R' ? 2 : 4;
          this.showToast(`Maximum ${max} copies atteint`);
        }
      }
    });

    modalRemoveBtn?.addEventListener('click', () => {
      if (this.currentCardBase) {
        this.deckManager.removeCard(this.currentCardBase.number);
        this.showToast('Carte retirée');
        this.updateModalDeckBadge(); // Mettre à jour l'indicateur du modal
      }
    });

    // Escape key to close modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeModal();
      }
    });
  }

  updateDeckDisplay(): void {
    const deckList = document.getElementById('deckList');
    if (!deckList) {
      return;
    }

    const deck = this.deckManager.getDeck();
    
    if (deck.length === 0) {
      deckList.innerHTML = `
        <div style="text-align: center; padding: 40px 20px; color: rgba(255,255,255,0.5); font-size: 13px;">
          Cliquez sur les cartes pour les ajouter à votre deck
        </div>
      `;
    } else {
      deckList.innerHTML = deck
        .sort((a, b) => a.number - b.number)
        .map(entry => {
          const card = this.cards.get(entry.number);
          if (!card) return '';

          return `
            <div class="deck-item" data-card="${card.number}">
              <div class="deck-item-qty">${entry.qty}x</div>
              <div class="deck-item-info">
                <div class="deck-item-name">${String(card.number).padStart(3, '0')} - ${card.name}</div>
                <div class="deck-item-meta">${card.rarity} • ${card.points}★ • Coût ${card.cost}</div>
              </div>
              <div class="deck-item-actions">
                <button class="btn-sm" data-action="add" data-card="${card.number}">+</button>
                <button class="btn-sm" data-action="remove" data-card="${card.number}">−</button>
              </div>
            </div>
          `;
        })
        .join('');

      // Add event listeners
      deckList.querySelectorAll('[data-action="add"]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const cardNum = parseInt((btn as HTMLElement).dataset.card || '0');
          if (this.deckManager.addCard(cardNum)) {
            this.showToast('Carte ajoutée !');
          } else {
            const card = this.cards.get(cardNum);
            const rarity = card?.rarity?.toUpperCase() || 'C';
            const max = rarity === 'R' ? 2 : 4;
            this.showToast(`Maximum ${max} copies atteint`);
          }
        });
      });

      deckList.querySelectorAll('[data-action="remove"]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const cardNum = parseInt((btn as HTMLElement).dataset.card || '0');
          this.deckManager.removeCard(cardNum);
        });
      });

      // Click on deck item to view card
      const items = deckList.querySelectorAll('.deck-item');
      
      items.forEach(item => {
        const cardNum = parseInt((item as HTMLElement).dataset.card || '0');
        const card = this.cards.get(cardNum);
        
        // Click on info section (name + meta)
        const infoEl = item.querySelector('.deck-item-info');
        if (infoEl && card) {
          (infoEl as HTMLElement).style.cursor = 'pointer';
          infoEl.addEventListener('click', (e) => {
            e.stopPropagation();
            this.showCardModal(card);
          });
        }
        
        // Click on qty section
        const qtyEl = item.querySelector('.deck-item-qty');
        if (qtyEl && card) {
          (qtyEl as HTMLElement).style.cursor = 'pointer';
          qtyEl.addEventListener('click', (e) => {
            e.stopPropagation();
            this.showCardModal(card);
          });
        }
      });
    }

    // Update stats
    this.updateStats();
  }

  private updateStats(): void {
    const total = this.deckManager.getTotalCards();
    const rares = this.deckManager.getTotalRares();
    const { legal } = this.deckManager.isLegal();


    const deckSize = document.getElementById('deckSize');
    const deckRares = document.getElementById('deckRares');
    const deckLegality = document.getElementById('deckLegality');

    if (deckSize) {
      deckSize.textContent = `${total}/40`;
      deckSize.className = total === 40 ? 'stat-value valid' : 'stat-value invalid';
    } else {
    }

    if (deckRares) {
      deckRares.textContent = `${rares}/12`;
      deckRares.className = rares <= 12 ? 'stat-value valid' : 'stat-value invalid';
    } else {
    }

    if (deckLegality) {
      deckLegality.textContent = legal ? '✓ Légal' : '✗ Illégal';
      deckLegality.className = legal ? 'stat-value valid' : 'stat-value invalid';
    } else {
    }
  }

  // base = identity for deck operations, shown = face to display (can differ for double-faced cards)
  showCardModal(base: Card, shown: Card = base): void {
    
    // FIX #3: Calculer face alternative (ne reset que si nouvelle carte)
    const isNewCard = !this.currentCardBase || this.currentCardBase.number !== base.number;
    if (isNewCard) this.showBackFace = false;
    
    const hasFlip = !!(shown.transformTo || shown.transformFrom);
    const otherFaceId = shown.transformTo || shown.transformFrom;
    const otherCard = (hasFlip && otherFaceId) ? this.cards.get(otherFaceId) : null;
    const displayCard = (this.showBackFace && otherCard) ? otherCard : shown;
    
    this.currentCardBase = base;
    this.currentCardShown = shown;

    const modal = document.getElementById('cardModal');
    const backdrop = document.getElementById('modalBackdrop');
    
    
    const modalCardImage = document.getElementById('modalCardImage') as HTMLImageElement;
    const modalCardName = document.getElementById('modalCardName');
    const modalCardMeta = document.getElementById('modalCardMeta');
    const modalCardText = document.getElementById('modalCardText');

    if (modalCardImage) {
      modalCardImage.src = displayCard.image || '';
      modalCardImage.alt = displayCard.name;
    }

    if (modalCardName) {
      // Supprimer ancien bouton flip s'il existe
      const existingFlip = modalCardName.parentElement?.querySelector('.flip-btn');
      if (existingFlip) existingFlip.remove();
      
      modalCardName.textContent = `${String(displayCard.number).padStart(3, '0')} - ${displayCard.name}`;
      
      // FIX #3: Ajouter bouton flip si carte a transformTo/From
      if (hasFlip && otherCard && modalCardName.parentElement) {
        const flipBtn = document.createElement('button');
        flipBtn.className = 'flip-btn' + (this.showBackFace ? ' is-back' : '');
        flipBtn.innerHTML = '↻';
        flipBtn.onclick = (e) => {
          e.stopPropagation();
          this.showBackFace = !this.showBackFace;
          this.showCardModal(base, shown);
        };
        modalCardName.parentElement.appendChild(flipBtn);
      }
    }

    if (modalCardMeta) {
      modalCardMeta.innerHTML = `
        <span class="modal-badge ${displayCard.rarity === 'R' ? 'rare' : 'common'}">${displayCard.rarity}</span>
        <span class="modal-badge">Coût ${displayCard.cost}</span>
        <span class="modal-badge">${displayCard.points}★</span>
        <span class="modal-badge">${displayCard.type}</span>
        <span class="modal-badge" style="background: rgba(102, 126, 234, 0.3);">Deck: ${this.deckManager.getCardQuantity(base.number)}/${(base.rarity?.toUpperCase() || 'C') === 'R' ? 2 : 4}</span>
      `;
    }

    if (modalCardText) {
      modalCardText.textContent = displayCard.text || 'Aucun effet spécial';
    }

    modal?.classList.add('active');
    backdrop?.classList.add('active');
  }

  closeModal(): void {
    const modal = document.getElementById('cardModal');
    const backdrop = document.getElementById('modalBackdrop');
    
    modal?.classList.remove('active');
    backdrop?.classList.remove('active');
    
    this.currentCardBase = null;
    this.currentCardShown = null;
  }

  // Met à jour l'indicateur "Deck: X/Y" dans le modal de carte
  private updateModalDeckBadge(): void {
    if (!this.currentCardBase) return;
    
    const modalCardMeta = document.getElementById('modalCardMeta');
    if (!modalCardMeta) return;
    
    const qty = this.deckManager.getCardQuantity(this.currentCardBase.number);
    const rarity = (this.currentCardBase.rarity?.toUpperCase() || 'C');
    const max = rarity === 'R' ? 2 : 4;
    
    // Trouver le badge "Deck: X/Y" et le mettre à jour
    const badges = modalCardMeta.querySelectorAll('.modal-badge');
    badges.forEach(badge => {
      if (badge.textContent?.startsWith('Deck:')) {
        badge.textContent = `Deck: ${qty}/${max}`;
      }
    });
  }

  private copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).catch(err => {
      console.error('Failed to copy:', err);
    });
  }

  private showToast(message: string): void {
    // Create toast element
    const toast = document.createElement('div');
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 30px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 12px 24px;
      border-radius: 25px;
      font-size: 14px;
      font-weight: 600;
      z-index: 10000;
      pointer-events: none;
      animation: slideUp 0.3s ease;
      border: 1px solid rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(10px);
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = 'slideDown 0.3s ease';
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 2000);
  }

  hideLoading(): void {
    const loading = document.getElementById('loading');
    if (loading) {
      loading.style.opacity = '0';
      setTimeout(() => {
        loading.style.display = 'none';
      }, 300);
    }
  }

  private initDeckPanelToggle(): void {
    const deckPanel = document.getElementById('deckPanel') as HTMLElement | null;
    const deckHeader = document.getElementById('deckHeader') as HTMLElement | null;
    const deckToggle = document.getElementById('deckToggle') as HTMLButtonElement | null;
    if (!deckPanel || !deckHeader || !deckToggle) return;

    const mq = window.matchMedia('(max-width: 768px)');

    const setState = (state: 'collapsed' | 'expanded') => {
  // Ensure exclusivity to avoid CSS glitches
  deckPanel.classList.remove('collapsed', 'expanded');
  deckPanel.classList.add(state);
};

const applyMode = () => {
  if (mq.matches) {
    // Mobile: default to collapsed to avoid blocking the grid
    if (!deckPanel.classList.contains('collapsed') && !deckPanel.classList.contains('expanded')) {
      setState('collapsed');
    } else {
      // If both got applied somehow (race), normalize
      if (deckPanel.classList.contains('collapsed') && deckPanel.classList.contains('expanded')) {
        setState('collapsed');
      }
    }
  } else {
    // Desktop: keep current state; if none is set, start expanded
    if (!deckPanel.classList.contains('collapsed') && !deckPanel.classList.contains('expanded')) {
      setState('expanded');
    } else if (deckPanel.classList.contains('collapsed') && deckPanel.classList.contains('expanded')) {
      // Normalize impossible state
      setState('expanded');
    }
  }
};

const toggle = () => {
  const isCollapsed = deckPanel.classList.contains('collapsed');
  setState(isCollapsed ? 'expanded' : 'collapsed');
};

    deckToggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggle();
    });

    deckHeader.addEventListener('click', (e) => {
      // On mobile, tapping the header toggles the panel
      if (!mq.matches) return;
      
      // Ne pas toggle si on clique sur l'input du nom
      const target = e.target as HTMLElement;
      if (target.id === 'deckNameInput' || target.closest('#deckNameInput')) {
        e.stopPropagation();
        return;
      }
      
      // avoid toggling when clicking interactive controls inside header (if any later)
      toggle();
    });

    // React to viewport changes
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', applyMode);
    } else {
      // Safari fallback
      // @ts-expect-error legacy
      mq.addListener(applyMode);
    }

    applyMode();
  }

  private openImportModal(): void {
    const modal = document.getElementById('importModal');
    const backdrop = document.getElementById('importModalBackdrop');
    const textarea = document.getElementById('importTextarea') as HTMLTextAreaElement;
    
    if (modal && backdrop) {
      modal.style.display = 'block';
      backdrop.style.display = 'block';
      
      if (textarea) {
        textarea.value = '';
        textarea.focus();
      }
    }
  }

  private closeImportModal(): void {
    const modal = document.getElementById('importModal');
    const backdrop = document.getElementById('importModalBackdrop');
    
    if (modal && backdrop) {
      modal.style.display = 'none';
      backdrop.style.display = 'none';
    }
  }

  private confirmImport(): void {
    const textarea = document.getElementById('importTextarea') as HTMLTextAreaElement;
    
    if (!textarea || !textarea.value.trim()) {
      this.showToast('Decklist vide');
      return;
    }

    const result = this.deckManager.importFromText(textarea.value);
    
    if (result.success) {
      this.showToast(result.message);
      this.closeImportModal();
      
      // Mettre à jour le nom affiché
      const deckNameInput = document.getElementById('deckNameInput') as HTMLInputElement;
      if (deckNameInput) {
        deckNameInput.value = this.deckManager.getDeckName();
      }
    } else {
      this.showToast('❌ ' + result.message);
    }
  }


  private initSupabaseButtons(): void {
    const btnLogin = document.getElementById('btnLogin') as HTMLButtonElement | null;
    const btnPwaRefresh = document.getElementById('btnPwaRefresh') as HTMLButtonElement | null;
    const btnCloudSave = document.getElementById('btnCloudSave') as HTMLButtonElement | null;
    const btnCloudLoad = document.getElementById('btnCloudLoad') as HTMLButtonElement | null;

    const refreshAuthUI = async () => {
      const user = await getSessionUser();
      const logged = !!user;
      if (btnLogin) {
        btnLogin.textContent = logged ? 'Connecté' : 'Connexion';
        btnLogin.classList.toggle('connected', logged);
        // Keep the button usable as a status indicator; allow click to logout via confirm.
        btnLogin.title = logged ? 'Connecté (cliquer pour se déconnecter)' : 'Se connecter';
      }
      if (btnCloudSave) btnCloudSave.disabled = !logged;
      if (btnCloudLoad) btnCloudLoad.disabled = !logged;
    };

    // initial state
    refreshAuthUI().catch(console.error);

    // Small in-app refresh button (helps a lot on PWA when caches get stuck)
    btnPwaRefresh?.addEventListener('click', () => {
      void this.hardRefreshPWA();
    });

    // Keep UI in sync with auth changes (works for future in-game usage too)
    supabase.auth.onAuthStateChange(() => {
      refreshAuthUI().catch(console.error);
    });

    btnLogin?.addEventListener('click', async () => {
      const user = await getSessionUser();
      if (user) {
        // optional: allow logout
        const doLogout = confirm('Déjà connecté. Se déconnecter ?');
        if (doLogout) {
          await logout();
          await refreshAuthUI();
          this.showToast('Déconnecté');
        }
        return;
      }

      // Playtest-friendly auth: email + password (no emails / no SMTP needed)
      this.openAuthModal(async (email, password) => {
        await signInOrCreateWithPassword(email, password);
        await refreshAuthUI();
        return { step: 'logged-in' };
      });
    });

    btnCloudSave?.addEventListener('click', async () => {
      try {
        const name = this.deckManager.getDeckName() || 'Nouveau deck';
        const customName = prompt('Nom du deck à sauvegarder :', name) || name;
        const res = await saveDeck(customName, {
          deck: this.deckManager.getDeck(),
          deckName: customName,
          timestamp: Date.now()
        });
        this.showToast(`Sauvegardé ☁️ (${res.name})`);
      } catch (e) {
        console.error(e);
        alert('Impossible de sauvegarder (êtes-vous connecté ?)');
      }
    });

    btnCloudLoad?.addEventListener('click', async () => {
      try {
        const decks = await listDecks();
        if (!decks.length) {
          alert('Aucun deck sauvegardé sur ce compte.');
          return;
        }
        const list = decks.map((d, i) => `${i+1}) ${d.name}`).join('\n');
        const pickRaw = prompt(
          `Mes decks :

${list}

➡️ Charger : entre un numéro (ex: 1)
🗑️ Supprimer : tape D puis un numéro (ex: D1)
`,
          '1'
        );
        const pick = (pickRaw || '').trim();
        if (!pick) return;

        // Delete flow: D<number>
        if (/^d\s*\d+$/i.test(pick)) {
          const n = parseInt(pick.replace(/[^0-9]/g, ''), 10);
          const idx = n - 1;
          if (isNaN(idx) || idx < 0 || idx >= decks.length) return;
          const ok = confirm(`Supprimer définitivement le deck "${decks[idx].name}" ?`);
          if (!ok) return;
          await deleteDeck(decks[idx].id);
          this.showToast(`Supprimé 🗑️ (${decks[idx].name})`);
          return;
        }

        // Load flow: <number>
        const idx = parseInt(pick, 10) - 1;
        if (isNaN(idx) || idx < 0 || idx >= decks.length) return;

        const full = await loadDeck(decks[idx].id);
        if (!full?.deck) {
          alert('Deck invalide.');
          return;
        }

        // Reset + import
        this.deckManager.clear();
        // Prefer structured deck if present
        if (Array.isArray(full.deck.deck)) {
          for (const entry of full.deck.deck) {
            // entry: {number, qty}
            if (typeof entry?.number === 'number' && typeof entry?.qty === 'number') {
              for (let k = 0; k < entry.qty; k++) this.deckManager.addCard(entry.number);
            }
          }
        } else if (typeof full.deckText === 'string') {
          // fallback if you later store text
          this.deckManager.importFromText(full.deckText);
        }

        if (full.name) this.deckManager.setDeckName(full.name);

        // Sync UI input with loaded deck name
        const deckNameInput = document.getElementById('deckNameInput') as HTMLInputElement | null;
        if (deckNameInput && full.name) deckNameInput.value = full.name;
        this.showToast(`Chargé ☁️ (${full.name})`);
      } catch (e) {
        console.error(e);
        alert('Impossible de charger (êtes-vous connecté ?)');
      }
    });
  }

  private openAuthModal(onSubmit: (email: string, password: string) => Promise<{ step: 'logged-in' }>): void {
    const modal = document.getElementById('authModal') as HTMLElement | null;
    const backdrop = document.getElementById('authModalBackdrop') as HTMLElement | null;
    const closeBtn = document.getElementById('authModalClose') as HTMLElement | null;
    const emailInput = document.getElementById('authEmail') as HTMLInputElement | null;
    const passInput = document.getElementById('authPassword') as HTMLInputElement | null;
    const btnSubmit = document.getElementById('authSubmit') as HTMLButtonElement | null;
    const msg = document.getElementById('authMsg') as HTMLElement | null;

    if (!modal || !backdrop || !emailInput || !passInput || !btnSubmit) {
      // Fallback (shouldn't happen)
      const email = prompt('Email :');
      if (!email) return;
      const password = prompt('Mot de passe :') || '';
      if (!password) return;
      signInOrCreateWithPassword(email.trim(), password)
        .then(() => alert('Connecté.'))
        .catch(() => alert('Erreur connexion'));
      return;
    }

    const setMsg = (t: string) => {
      if (msg) msg.textContent = t;
    };

    const setBusy = (busy: boolean) => {
      btnSubmit.disabled = busy;
      emailInput.disabled = busy;
      passInput.disabled = busy;
    };

    const close = () => {
      modal.style.display = 'none';
      backdrop.style.display = 'none';
    };

    const open = () => {
      modal.style.display = 'block';
      backdrop.style.display = 'block';
      setMsg('');
      emailInput.value = emailInput.value || '';
      passInput.value = '';
      setBusy(false);
      emailInput.focus();
    };

    const normalizeEmail = () => emailInput.value.trim();

    const submit = async () => {
      const email = normalizeEmail();
      const password = passInput.value;
      if (!email) {
        setMsg('❌ Email requis');
        return;
      }
      if (!password) {
        setMsg('❌ Mot de passe requis');
        return;
      }
      try {
        setBusy(true);
        setMsg('Connexion…');
        const res = await onSubmit(email, password);
        if (res.step === 'logged-in') {
          setMsg('✅ Connecté !');
          setTimeout(() => close(), 450);
          this.showToast('Connecté');
        }
      } catch (e: any) {
        console.error(e);
        setMsg('❌ ' + (e?.message || 'Erreur connexion'));
      } finally {
        setBusy(false);
      }
    };

    // Bind (idempotent-ish)
    btnSubmit.onclick = () => { void submit(); };
    closeBtn && (closeBtn.onclick = () => close());
    backdrop.onclick = () => close();
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.style.display === 'block') close();
      if (e.key === 'Enter' && modal.style.display === 'block') {
        void submit();
      }
    });

    open();
  }

  private async hardRefreshPWA(): Promise<void> {
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) await reg.update();
      }
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) {
      console.warn('PWA refresh failed', e);
    } finally {
      window.location.reload();
    }
  }

}
